import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { dashboardAuthUrl, fetchAuthJson } from '../api/authBase';

const TOKEN_KEY = 'dashboard_auth_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(Boolean(sessionStorage.getItem(TOKEN_KEY)));

  const persistToken = useCallback((t) => {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
    setToken(t || '');
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setBootstrapping(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(dashboardAuthUrl('/api/auth/me'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          persistToken('');
          setUser(null);
          return;
        }
        const data = await res.json();
        setUser(data.user);
      } catch {
        if (!cancelled) {
          persistToken('');
          setUser(null);
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, persistToken]);

  const login = useCallback(async (email, password) => {
    let res;
    try {
      res = await fetch(dashboardAuthUrl('/api/auth/login'), {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ email, password }),
      });
    } catch (e) {
      const msg = e?.message || '';
      if (/failed to fetch|networkerror|load failed/i.test(msg)) {
        throw new Error(
          `Network error — check REACT_APP_API_URL (e.g. http://localhost:3002) and that backdashboard is running (\`npm start\`).`
        );
      }
      throw e;
    }
    const data = await fetchAuthJson(res);
    persistToken(data.token);
    setUser(data.user);
    setBootstrapping(false);
  }, [persistToken]);

  const register = useCallback(async (email, password) => {
    let res;
    try {
      res = await fetch(dashboardAuthUrl('/api/auth/register'), {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ email, password }),
      });
    } catch (e) {
      const msg = e?.message || '';
      if (/failed to fetch|networkerror|load failed/i.test(msg)) {
        throw new Error(
          `Network error — check REACT_APP_API_URL (e.g. http://localhost:3002) and that backdashboard is running (\`npm start\`).`
        );
      }
      throw e;
    }
    const data = await fetchAuthJson(res);
    persistToken(data.token);
    setUser(data.user);
    setBootstrapping(false);
  }, [persistToken]);

  const logout = useCallback(() => {
    persistToken('');
    setUser(null);
  }, [persistToken]);

  const value = useMemo(
    () => ({
      token,
      user,
      bootstrapping,
      login,
      register,
      logout,
      isAuthenticated: Boolean(token && user),
    }),
    [token, user, bootstrapping, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
